import * as React from 'react'
import {
    Alert,
    Container,
    Grid2,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material'

import Navbar from '../setup/Navbar'
import HeadingText from '../../BuiltInComponent/HeadingTest'
import ButtonNormal from '../../BuiltInComponent/ButtonNormal'

import { useDispatch, useSelector } from 'react-redux'
import { createBatch, getBatchByID, getBatches, getFormulaByID, getFormulas } from '../../features/AdminManagement/productionSlice'

const emptyOutput = { outputItemCode: '', actualQtyProduced: '', wastageQty: '', unit: '' }

export default function ProductionBatches() {
    const dispatch = useDispatch()
    const { formulas, selectedFormula, batches, selectedBatch, isLoading } = useSelector((state) => state.production)

    const [alertState, setAlertState] = React.useState({ type: '', message: '' })
    const [form, setForm] = React.useState({
        formulaID: '',
        batchDate: new Date().toISOString().slice(0, 10),
        branchID: 1,
        inputQtyUsed: '',
        outputs: [{ ...emptyOutput }]
    })

    React.useEffect(() => {
        dispatch(getFormulas())
        dispatch(getBatches())
    }, [dispatch])

    React.useEffect(() => {
        if (!selectedFormula?.master) return

        const mappedOutputs = (selectedFormula.outputs || []).map((o) => ({
            outputItemCode: o.outputItemCode,
            actualQtyProduced: o.expectedQty,
            wastageQty: 0,
            unit: o.outputUnit || ''
        }))

        setForm((prev) => ({
            ...prev,
            formulaID: selectedFormula.master.formulaID,
            inputQtyUsed: selectedFormula.master.inputQuantity,
            outputs: mappedOutputs.length > 0 ? mappedOutputs : [{ ...emptyOutput }]
        }))
    }, [selectedFormula])

    const onFormulaChange = (formulaID) => {
        setForm((prev) => ({ ...prev, formulaID }))
        if (formulaID) {
            dispatch(getFormulaByID(formulaID))
        }
    }

    const validate = () => {
        if (!form.formulaID) return 'Formula is required'
        if (!form.batchDate) return 'Batch date is required'
        if (!form.inputQtyUsed || Number(form.inputQtyUsed) <= 0) return 'Input quantity must be greater than 0'

        for (const row of form.outputs) {
            if (!row.outputItemCode) return 'Output item is required'
            if (!row.actualQtyProduced || Number(row.actualQtyProduced) <= 0) return 'Actual output qty must be greater than 0'
            if (!row.unit || !String(row.unit).trim()) return 'Output unit is required'
        }

        return null
    }

    const onSaveBatch = async () => {
        setAlertState({ type: '', message: '' })
        const error = validate()
        if (error) {
            setAlertState({ type: 'error', message: error })
            return
        }

        const payload = {
            formulaID: Number(form.formulaID),
            batchDate: form.batchDate,
            branchID: Number(form.branchID || 1),
            inputQtyUsed: Number(form.inputQtyUsed),
            outputs: form.outputs.map((o) => ({
                outputItemCode: Number(o.outputItemCode),
                actualQtyProduced: Number(o.actualQtyProduced),
                wastageQty: Number(o.wastageQty || 0),
                unit: String(o.unit).trim()
            }))
        }

        const result = await dispatch(createBatch(payload))
        if (result.type.endsWith('fulfilled')) {
            setAlertState({ type: 'success', message: 'Batch posted successfully' })
            dispatch(getBatches())
        } else {
            setAlertState({ type: 'error', message: result.payload || 'Failed to post batch' })
        }
    }

    const updateOutput = (index, key, value) => {
        setForm((prev) => ({
            ...prev,
            outputs: prev.outputs.map((row, i) => (i === index ? { ...row, [key]: value } : row))
        }))
    }

    const selectedFormulaName = formulas.find((f) => Number(f.formulaID) === Number(form.formulaID))?.formulaName || ''

    return (
        <>
            <Navbar />
            <Container sx={{ mt: 2, mb: 4 }}>
                <HeadingText text='Production Batch Posting' />

                {alertState.message && (
                    <Alert severity={alertState.type || 'info'} sx={{ mt: 2 }}>{alertState.message}</Alert>
                )}

                <Grid2 container spacing={2} sx={{ mt: 1 }}>
                    <Grid2 size={{ xs: 12, md: 7 }}>
                        <Paper variant='outlined' sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 700, mb: 2 }}>Create Batch</Typography>
                            <Grid2 container spacing={2}>
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        select
                                        label='Formula'
                                        fullWidth
                                        size='small'
                                        value={form.formulaID}
                                        onChange={(e) => onFormulaChange(e.target.value)}
                                    >
                                        {formulas.map((f) => (
                                            <MenuItem key={f.formulaID} value={f.formulaID}>
                                                {f.formulaName}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        label='Batch Date'
                                        type='date'
                                        fullWidth
                                        size='small'
                                        value={form.batchDate}
                                        onChange={(e) => setForm((prev) => ({ ...prev, batchDate: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 3 }}>
                                    <TextField
                                        label='Input Qty Used'
                                        type='number'
                                        fullWidth
                                        size='small'
                                        value={form.inputQtyUsed}
                                        onChange={(e) => setForm((prev) => ({ ...prev, inputQtyUsed: e.target.value }))}
                                    />
                                </Grid2>

                                <Grid2 size={12}>
                                    <Typography sx={{ fontWeight: 700 }}>Batch Outputs</Typography>
                                </Grid2>

                                {form.outputs.map((row, idx) => (
                                    <React.Fragment key={`batch-out-${idx}`}>
                                        <Grid2 size={{ xs: 12, md: 5 }}>
                                            <TextField
                                                label='Output Item Code'
                                                fullWidth
                                                size='small'
                                                value={row.outputItemCode}
                                                disabled
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 6, md: 3 }}>
                                            <TextField
                                                label='Actual Qty'
                                                type='number'
                                                fullWidth
                                                size='small'
                                                value={row.actualQtyProduced}
                                                onChange={(e) => updateOutput(idx, 'actualQtyProduced', e.target.value)}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 6, md: 2 }}>
                                            <TextField
                                                label='Wastage'
                                                type='number'
                                                fullWidth
                                                size='small'
                                                value={row.wastageQty}
                                                onChange={(e) => updateOutput(idx, 'wastageQty', e.target.value)}
                                            />
                                        </Grid2>
                                        <Grid2 size={{ xs: 12, md: 2 }}>
                                            <TextField
                                                label='Unit'
                                                fullWidth
                                                size='small'
                                                value={row.unit}
                                                onChange={(e) => updateOutput(idx, 'unit', e.target.value)}
                                            />
                                        </Grid2>
                                    </React.Fragment>
                                ))}

                                <Grid2 size={{ xs: 12, md: 4 }}>
                                    <ButtonNormal onClick={onSaveBatch} text={isLoading ? 'Posting...' : 'Post Batch'} />
                                </Grid2>
                                <Grid2 size={{ xs: 12, md: 8 }}>
                                    <Typography sx={{ mt: 1, color: '#555' }}>
                                        Selected Formula: {selectedFormulaName || 'N/A'}
                                    </Typography>
                                </Grid2>
                            </Grid2>
                        </Paper>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 5 }}>
                        <Paper variant='outlined' sx={{ p: 2 }}>
                            <Typography sx={{ fontWeight: 700, mb: 1 }}>Recent Batches</Typography>
                            <TableContainer sx={{ maxHeight: 540 }}>
                                <Table size='small' stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700 }}>Batch ID</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }}>Formula</TableCell>
                                            <TableCell sx={{ fontWeight: 700 }} align='right'>Output</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {batches.map((b) => (
                                            <TableRow
                                                key={b.batchID}
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => dispatch(getBatchByID(b.batchID))}
                                            >
                                                <TableCell>{b.batchID}</TableCell>
                                                <TableCell>{b.batchDate}</TableCell>
                                                <TableCell>{b.formulaName}</TableCell>
                                                <TableCell align='right'>{Number(b.totalOutputQty || 0).toFixed(3)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid2>

                    {selectedBatch?.master && (
                        <Grid2 size={12}>
                            <Paper variant='outlined' sx={{ p: 2 }}>
                                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                                    Batch Detail #{selectedBatch.master.batchID}
                                </Typography>
                                <Typography sx={{ mb: 1 }}>
                                    Formula: {selectedBatch.master.formulaName} | Input: {selectedBatch.master.inputItemDesc} | Qty Used: {selectedBatch.master.inputQtyUsed}
                                </Typography>
                                <TableContainer>
                                    <Table size='small'>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700 }}>Output Item</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align='right'>Actual Qty</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }} align='right'>Wastage</TableCell>
                                                <TableCell sx={{ fontWeight: 700 }}>Unit</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(selectedBatch.outputs || []).map((row) => (
                                                <TableRow key={row.batchOutputID}>
                                                    <TableCell>{row.outputItemDesc}</TableCell>
                                                    <TableCell align='right'>{Number(row.actualQtyProduced || 0).toFixed(3)}</TableCell>
                                                    <TableCell align='right'>{Number(row.wastageQty || 0).toFixed(3)}</TableCell>
                                                    <TableCell>{row.unit}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid2>
                    )}
                </Grid2>
            </Container>
        </>
    )
}
